import { UserMenu } from './UserMenu';

import { Button } from './ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { useState } from 'react';
import { AuthSheet } from './auth/AuthSheet';
import { Link, useLocation } from 'react-router-dom';
import { PrefetchLink } from './PrefetchLink';
import { routePrefetch } from '@/lib/routePrefetch';
import { cn } from '@/lib/utils';
import { MobileMenu } from './MobileMenu';
import { GuestMenu } from './GuestMenu';
import { toolbarPopoverButton } from '@/lib/buttonStyles';
import { SoftBackButton } from './SoftBackButton';
import { useMobileChromeOffset } from '@/hooks/useMobileChromeVisible';
import { useKiosk } from '@/context/KioskContext';
import { RETAIL_MODE } from '@/lib/retailMode';
interface HeaderProps {
  fixedOnDesktop?: boolean;
  showBackButton?: boolean;
  onBackClick?: () => void;
  /** Explicit label for the back button (e.g. "Back to Settings"). Overrides derived labels. */
  backLabel?: string;
  noBorder?: boolean;
  hideOnScrollMobile?: boolean;
  hideOnMobile?: boolean;
  /** Hide the CardSimp wordmark on mobile (e.g. on app-like detail pages). Desktop is unaffected. */
  hideLogoMobile?: boolean;
  /** Always render the hamburger menu on mobile, even for signed-in users. */
  alwaysShowMobileMenu?: boolean;
  /** Hide the hamburger menu on mobile entirely (e.g. detail pages where the global Me nav covers auth). */
  hideMobileMenu?: boolean;
}
export const Header = ({
  fixedOnDesktop = false,
  showBackButton = false,
  onBackClick,
  backLabel,
  noBorder = false,
  hideOnScrollMobile = false,
  hideOnMobile = false,
  hideLogoMobile = false,
  alwaysShowMobileMenu = false,
  hideMobileMenu = false

}: HeaderProps) => {
  useMobileChromeOffset();
  const {
    user
  } = useAuth();
  const {
    profile
  } = useProfile();
  const { isAdmin } = useIsAdmin();
  const username = profile?.username;
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const location = useLocation();
  const { isKiosk, ownerUsername } = useKiosk();
  const kioskHome = isKiosk ? `/s/${ownerUsername}` : '/';

  // Check if we're on the inventory or storefront
  const isInventoryActive = location.pathname === '/inventory';
  const isStorefrontActive = location.pathname.startsWith('/u/') || location.pathname === '/preview';
  const isAdminActive = location.pathname === '/admin';
  const isSellersActive = location.pathname === '/market' || location.pathname.startsWith('/market/') || location.pathname.startsWith('/item/');
  return <>
			<header
				style={{
					paddingTop: 'env(safe-area-inset-top)',
					...(hideOnScrollMobile && {
						transform: 'translate3d(0, calc(var(--mobile-chrome-offset, 0px) * -1), 0)',
						marginBottom: 'calc(var(--mobile-chrome-offset, 0px) * -1)',
						willChange: 'transform',
					}),
				}}
				className={cn(
					'h-12 md:h-[70px] pl-3 pr-3 md:px-6 flex items-center gap-4 md:gap-6 z-40 flex-shrink-0 relative overflow-hidden',
					'bg-chrome',
					fixedOnDesktop && 'lg:fixed lg:top-0 lg:left-0 lg:right-0',
					hideOnScrollMobile && 'md:!mt-0 md:!mb-0 md:!translate-y-0',
					hideOnMobile && 'hidden md:flex'
				)}
			>

				{/* Progress-faded bottom hairline — dims as the header retracts so it
				    doesn't pop out the instant the offset trips. Desktop keeps its
				    full-opacity hairline via the md: override. */}
				{!noBorder && (
					<span
						aria-hidden="true"
						className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-white/[0.06] md:bg-white/[0.08] md:!opacity-100"
						style={{
							opacity: hideOnScrollMobile
								? 'calc(1 - var(--mobile-chrome-progress, 0))'
								: 1,
						}}
					/>
				)}
				{/* Left: Back button (mobile only) or Logo */}
				<div className="flex items-center gap-2 md:gap-3">
					{/* Back button - Mobile only (canonical SoftBackButton ghost pill) */}
				{showBackButton && onBackClick && (
					<SoftBackButton onClick={onBackClick} label={backLabel} fallbackLabel="Back" className="md:hidden flex-shrink-0" />

				)}

				{/* Logo - Left-aligned on all devices, smaller on mobile for app feel */}
				{!hideLogoMobile && (
					<Link to={kioskHome} className="md:hidden flex items-center group">
						<span className="font-logo tracking-tight text-[23.5px] font-bold leading-none [text-shadow:0_1px_2px_rgba(0,0,0,0.55),0_2px_10px_rgba(0,0,0,0.35)]">
							<span className="text-white dark:text-white">Card</span>
							<span className="text-primary">Simp</span>
						</span>
					</Link>
				)}

        <Link to={kioskHome} className="hidden md:flex items-center group" title="Go to Inventory" aria-label="CardSimp — back to Inventory">
          <span className="font-logo text-[23.5px] lg:text-[27.5px] font-bold tracking-tight [text-shadow:0_1px_2px_rgba(0,0,0,0.40),0_2px_14px_rgba(0,0,0,0.20)]">
            <span className="text-white">Card</span>
            <span className="text-primary">Simp</span>
          </span>
        </Link>
				</div>

				{/* Kiosk disclaimer: replaces nav + user menu when viewing a shared store */}
				{isKiosk && (
					<div className="ml-auto flex items-center min-w-0 md:min-w-0 md:flex-shrink-0">
						<span className="truncate md:whitespace-nowrap md:overflow-visible text-[11px] md:text-xs text-muted-foreground/80">
							Viewing <span className="text-foreground font-medium">@{ownerUsername}</span>'s store ·{' '}
							<Link to="/" className="text-active-gold hover:underline whitespace-nowrap">Pre-register for your own</Link>
						</span>
					</div>
				)}


				{/* Right: Mobile Menu / Desktop User Menu (hidden in kiosk) */}
				{!isKiosk && (
				<div className="flex items-center justify-end gap-2 md:gap-3 ml-auto">
					{/* Mobile hamburger: hidden when hideMobileMenu; otherwise signed-out always, signed-in only when alwaysShowMobileMenu */}
					{!hideMobileMenu && (!user || alwaysShowMobileMenu) && (
						<div className="md:hidden">
							<MobileMenu triggerVariant="toolbar" />
						</div>
					)}

					{/* Mobile Sign In fallback: signed-out users on pages that suppress the hamburger still need an entry point.
					    Retail mode: sign-in is hidden — owner uses /owner. */}
					{!user && hideMobileMenu && !RETAIL_MODE && (
						<div className="md:hidden">
							<Button onClick={() => setAuthDialogOpen(true)} variant="outline" size="sm" className={cn(toolbarPopoverButton, 'h-9 px-3 text-xs')}>
								Sign in
							</Button>
						</div>
					)}

					{/* Desktop right side: signed-in → full UserMenu; signed-out → hamburger with Page Owner Login */}
					<div className="hidden md:flex items-center gap-1 md:mt-[3px] md:mr-1">
						{user ? (
							<UserMenu />
						) : (
							<GuestMenu />
						)}
					</div>
				</div>
				)}
			</header>

			{/* Seam-cap: 1px chrome strip that rides with the header so any sub-pixel
			    drift between the retracting header and the rising sticky toolbar
			    reveals chrome color instead of inventory pixels. */}
			{hideOnScrollMobile && (
				<div
					aria-hidden
					className="md:hidden h-px -mt-px bg-chrome flex-shrink-0 relative z-30"
					style={{
						transform: 'translate3d(0, calc(var(--mobile-chrome-offset, 0px) * -1), 0)',
						marginBottom: 'calc(var(--mobile-chrome-offset, 0px) * -1)',
					}}
				/>
			)}

			<AuthSheet open={authDialogOpen} onOpenChange={setAuthDialogOpen} />
		</>;
};
